import React, {useEffect, useState} from 'react';
import {Box, Stack} from "@mui/material";
import {Controller, useForm} from "react-hook-form";
import {useQueryClient} from "@tanstack/react-query";
import {useToast} from "../../../../../../utils/contexts/toast-context";
import {
    ISchedaCosto
} from "../../../../../../utils/interfaces/tannery_production/order_schedule/schede_costo/ISchedaCosto";
import useApiGetSchedaCostoDetail from "./api-hooks/useApiGetSchedaCostoDetail";
import SaveOrBackForm from "../../../../../ui/common_components/SaveOrBackForm";
import useApiCreateSchedaCosto from "./api-hooks/useApiCreateSchedaCosto";
import useApiUpdateSchedaCosto from "./api-hooks/useApiUpdateSchedaCosto";
import useApiDeleteSchedaCosto from "./api-hooks/useApiDeleteSchedaCosto";
import MuiAlertDialog from "../../../../../ui/mui/mui_modal/MuiAlertDialog";
import MuiInputTextField from "../../../../../ui/mui/mui-input/MuiInputTextField";
import VociSchedaCostoTable from "./voci_scheda_costo/VociSchedaCostoTable";
import {useTanneryProduction} from "../../../../../../utils/contexts/tannery_production/tannery_production-context";


interface Props {
    order_id: number | undefined;
    onClose: () => void;
}


const SchedaCostoForm: React.FC<Props> = ({order_id, onClose}) => {
    const {selectedCompanyOrderSchedule,} = useTanneryProduction()


    const {displayMessage, displayError} = useToast();
    const queryClient = useQueryClient();
    const {control, handleSubmit, setValue} = useForm<ISchedaCosto>({});
    const [isCreating, setIsCreating] = useState(false);


    const {data: detailedObj, isSuccess, isFetched} = useApiGetSchedaCostoDetail(order_id);

    const [isConfirmationOpen, setConfirmationOpen] = useState(false);
    const deleteMutation = useApiDeleteSchedaCosto(detailedObj?.id);
    const {mutateAsync: apiDelete} = deleteMutation;
    const handleCancelDelete = () => {
        setConfirmationOpen(false);
    };
    const handleConfirmDelete = async () => {
        if (detailedObj) {
            try {
                await apiDelete({pk: detailedObj.id});
            } catch (error) {
            }
            setConfirmationOpen(false);
            onClose();
            await queryClient.invalidateQueries({queryKey: ['getCompanyOrderSchedule']});
            await queryClient.invalidateQueries({queryKey: ['useApiGetSchedaCostoDetail', selectedCompanyOrderSchedule?.id]});
            await queryClient.removeQueries({queryKey: ['useApiGetSchedaCostoDetail', order_id]});

        }

    };

    const createMutation = useApiCreateSchedaCosto();
    const updateMutation = useApiUpdateSchedaCosto();

    // Create if not found after successful query
    useEffect(() => {
        const createIfNotFound = async () => {
            if (isFetched && !detailedObj && order_id && !isCreating) {
                setIsCreating(true);
                try {
                    const mutation_data = {
                        order_id: order_id,
                    };
                    const newObj = await createMutation.mutateAsync(mutation_data);
                    displayMessage('added successfully');
                    await queryClient.invalidateQueries({queryKey: ['getCompanyOrderSchedule']});

                    // Invalidate queries to reload the newly created object
                    await queryClient.invalidateQueries({queryKey: ['useApiGetSchedaCostoDetail', order_id]});
                } catch (error) {
                    displayError('Error creating scheda costo');
                } finally {
                    setIsCreating(false);
                }
            }
        };

        createIfNotFound();
    }, [isFetched, detailedObj, order_id, isCreating]);

    useEffect(() => {
        if (detailedObj) {
            Object.entries(detailedObj).forEach(([key, value]) => {
                setValue(key as keyof ISchedaCosto, value);
            });
        }
    }, [detailedObj, setValue]);

    const onSubmit = async (formData: ISchedaCosto) => {
        const cleanValue = (value: any): any => {
            if (value === '' || value === undefined) {
                return null;
            }
            return value;
        };

        const mutation_data = {
            ...formData,
            order_id: order_id,
            misura_finale: cleanValue(formData.misura_finale),
            kg: cleanValue(formData.kg),
            pcs: cleanValue(formData.pcs),
            misura_iniziale: cleanValue(formData.misura_iniziale),
            prezzo_vendita: cleanValue(formData.prezzo_vendita),
        };

        try {
            if (detailedObj) {
                await updateMutation.mutateAsync({
                    id: detailedObj.id,
                    data: mutation_data
                });
                await queryClient.invalidateQueries({queryKey: ["useApiGetSchedaCostoDetail", order_id]});
                displayMessage('updated successfully');
            } else {
                await createMutation.mutateAsync({data: mutation_data});
                displayMessage('added successfully');
            }
            onClose();
            await queryClient.invalidateQueries({queryKey: ['useApiGetSchedaCostoDetail', order_id]});
        } catch (error) {
        }
    };


    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{padding: 2, minWidth: 400}}>

                <Stack direction={{xs: "column", md: "row"}} spacing={2}>
                    <Box flex={1}>
                        <Controller
                            name="data_scheda_costo"
                            control={control}
                            render={({field}) => (
                                <MuiInputTextField
                                    label="Data Scheda Costo"
                                    type="date"
                                    {...field}

                                />

                            )}
                        />
                    </Box>


                </Stack>


                <Stack direction={{xs: "column", md: "row"}} spacing={2}>
                    <Box flex={1}>
                        <Controller
                            name="pcs"
                            control={control}
                            render={({field}) => (
                                <MuiInputTextField
                                    label="PZ"
                                    type="number"
                                    {...field}

                                />

                            )}
                        />
                    </Box>
                    <Box flex={1}>
                        <Controller
                            name="kg"
                            control={control}
                            render={({field}) => (
                                <MuiInputTextField
                                    label="kg"
                                    type="number"
                                    {...field}

                                />

                            )}
                        />
                    </Box>
                </Stack>
                <Stack direction={{xs: "column", md: "row"}} spacing={2}>
                    <Box flex={1}>
                        <Controller
                            name="misura_iniziale"
                            control={control}
                            render={({field}) => (
                                <MuiInputTextField
                                    label="misura iniziale"
                                    type="number"
                                    {...field}

                                />

                            )}
                        />
                    </Box>
                    <Box flex={1}>
                        <Controller
                            name="misura_finale"
                            control={control}
                            render={({field}) => (
                                <MuiInputTextField
                                    label="misura finale"
                                    type="number"
                                    {...field}

                                />

                            )}
                        />
                    </Box>
                </Stack>
                <Stack direction={{xs: "column", md: "row"}} spacing={2}>
                    <Box flex={1}>
                        {/*<VociCostoSelect obj={undefined}*/}
                        {/*                 onChange={(obj) => {*/}
                        {/*                 }}*/}
                        {/*                 className={''}*/}
                        {/*/>*/}
                    </Box>
                    <Box flex={1}>
                        <Controller
                            name="prezzo_vendita"
                            control={control}
                            render={({field}) => (
                                <MuiInputTextField
                                    label="prezzo vendita"
                                    type="number"
                                    {...field}

                                />

                            )}
                        />
                    </Box>
                </Stack>

                <VociSchedaCostoTable scheda_costo_id={detailedObj?.id}/>


                <Stack spacing={2}>
                    <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1}}>
                        <SaveOrBackForm
                            onSubmit={onSubmit}
                            onClose={onClose}
                            handleSubmit={handleSubmit}
                            showDelete={!!detailedObj}
                            onDelete={() => setConfirmationOpen(true)}
                            saveOnClose={true}
                        />
                    </Box>

                </Stack>


            </Box>

            <MuiAlertDialog
                open={isConfirmationOpen}
                title="delete_confirmation"
                content="ask_for_delete"
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
            />
        </form>
    );
};

export default SchedaCostoForm;